import { hasUnsentTime, publishSeconds } from "../timesheet";
import type { RowOutcome, TaskListState, TimesheetRow as Row } from "../timesheet";

import styles from "./RowTable.module.css";
import { TimesheetRow } from "./TimesheetRow";

import { PlusIcon } from "@/components/icons";

/** The seconds Accelo will hold for a send: it stores whole minutes and rounds up. */
export function acceloSeconds(seconds: number): number {
  return Math.ceil(seconds / 60) * 60;
}

/** Whole minutes of seconds as `1h 30m`, `2h`, or `2m`. */
function formatHeld(seconds: number): string {
  const minutes = Math.round(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  if (hours === 0) return `${rest}m`;
  return rest === 0 ? `${hours}h` : `${hours}h ${rest}m`;
}

/**
 * Which box this is. `live` is the box for work in flight: it carries the
 * heading with Add task, the selection column and the publish band. `landed`
 * is the box for rows Accelo holds and that are finished with: rows alone.
 */
export type RowTableVariant = "live" | "landed";

interface RowTableProps {
  /** Newest first. */
  rows: readonly Row[];
  /** Which box to draw. Defaults to `live`. */
  variant?: RowTableVariant;
  /**
   * The rows an outcome line reads its name from. Defaults to `rows`. The page
   * hands the whole shown day, so a row that lands and leaves this box for the
   * landed box still keeps its name in the summary.
   */
  outcomeRows?: readonly Row[] | undefined;
  /** The page's one clock, in epoch milliseconds. */
  now: number;
  /** The Accelo task list, handed to every row. */
  tasks: TaskListState;
  /** What the last publish did to each row, by row id. */
  outcomes?: Readonly<Record<string, RowOutcome>>;
  /** A deleted row had landed time that Accelo keeps. */
  deletedLanded?: boolean;
  /** A publish is in flight: every row holds still until it answers. */
  publishing?: boolean;
  /** How many rows are ready to publish. */
  publishReady?: number;
  /**
   * The ids of the rows the person has chosen to publish. With it, the panel
   * lists what the press sends. Without it, the footer carries the button alone.
   */
  selected?: readonly string[] | undefined;
  onSelect?: ((id: string, next: boolean) => void) | undefined;
  /** Add a row. The landed box draws no Add task, so it needs none. */
  onAdd?: (() => void) | undefined;
  onRename: (id: string, name: string) => void;
  onRetask: (id: string, taskId: string) => void;
  onToggle: (id: string) => void;
  onPublish?: () => void;
  /** Stop a publish in flight. The button shows only while one is. */
  onStop?: (() => void) | undefined;
  /** Remove a row. Without it a row draws no delete. */
  onDelete?: ((id: string) => void) | undefined;
  /** Move a row to a day, `YYYY-MM-DD`. */
  onSetDate?: ((id: string, day: string) => void) | undefined;
  /** Set a row's elapsed time, in milliseconds. */
  onSetElapsed?: ((id: string, ms: number) => void) | undefined;
  /**
   * Move the row's start to a 24-hour `HH:MM` on its own day. An empty string is
   * the Automatic option: it drops the pick and the row reads its own start.
   */
  onSetStart?: ((id: string, hhmm: string) => void) | undefined;
}

/**
 * The table card: its heading with Add task, the rows, and a footer that
 * carries the publish panel on the left and, on the right, Stop publish beside
 * Publish to Accelo. Stop shows only while a publish is in flight.
 * The button is off while no row is ready, while a publish is in flight, and
 * while the page hands it no press. Given `selected`, the panel lists each
 * chosen row with the time Accelo will hold, and the button is off while that
 * list is empty.
 *
 * The `landed` variant is the same table without that chrome: its own heading,
 * no Add task, no selection column and no publish band. Its rows keep every
 * control, so pressing play there sends the row back to the live box.
 */
export function RowTable({
  rows,
  variant = "live",
  outcomeRows,
  now,
  tasks,
  outcomes,
  deletedLanded = false,
  publishing = false,
  publishReady = 0,
  selected,
  onSelect,
  onAdd,
  onRename,
  onRetask,
  onToggle,
  onPublish,
  onStop,
  onDelete,
  onSetDate,
  onSetElapsed,
  onSetStart,
}: RowTableProps) {
  const landedBox = variant === "landed";
  // A row with no unsent time is never sent, so it is never listed.
  const chosen =
    selected === undefined
      ? undefined
      : rows
          .filter((row) => selected.includes(row.id) && hasUnsentTime(row, now))
          .map((row) => ({
            id: row.id,
            name: row.name.trim() === "" ? "Unnamed row" : row.name,
            held: acceloSeconds(publishSeconds(row, now)),
            running: row.running,
          }));
  const nothingToSend = chosen === undefined ? publishReady === 0 : chosen.length === 0;
  const named = outcomeRows ?? rows;
  const outcomeLines = Object.entries(outcomes ?? {}).flatMap(([id, outcome]) => {
    const row = named.find((candidate) => candidate.id === id);
    return row === undefined
      ? []
      : [{ name: row.name.trim() === "" ? "Unnamed row" : row.name, outcome }];
  });
  const landedCount = outcomeLines.filter(
    ({ outcome }) => outcome.kind === "published" || outcome.kind === "already-published"
  ).length;
  const skippedCount = outcomeLines.filter(({ outcome }) => outcome.kind === "skipped").length;
  const failed = outcomeLines.filter(
    ({ outcome }) =>
      outcome.kind === "failed" ||
      outcome.kind === "landed-unconfirmed" ||
      outcome.kind === "unnamed"
  );

  return (
    <section
      className={styles.card}
      aria-labelledby={landedBox ? "timesheet-landed-title" : "timesheet-rows-title"}
    >
      <header className={styles.head}>
        {landedBox ? (
          <h2 id="timesheet-landed-title" className={styles.title}>
            Landed in Accelo
          </h2>
        ) : (
          <h2 id="timesheet-rows-title" className={styles.title}>
            Running tasks
          </h2>
        )}
        {!landedBox && onAdd !== undefined && (
          <button type="button" className={styles.add} onClick={onAdd}>
            <PlusIcon className={styles.addIcon} />
            Add task
          </button>
        )}
      </header>

      <div className={styles.columns} aria-hidden="true">
        <span />
        <span className={styles.column}>TASK NAME</span>
        <span className={styles.column}>ACCELO TASK</span>
        <span className={`${styles.column} ${styles.columnIcon}`}>DAY</span>
        <span className={`${styles.column} ${styles.columnEnd}`}>START</span>
        <span className={`${styles.column} ${styles.columnEnd}`}>ELAPSED</span>
        <span />
        <span />
        <span />
      </div>

      {rows.length === 0 ? (
        // The landed box is only drawn when it holds a row, so it says nothing here.
        !landedBox && (
          <div className={styles.empty}>
            <p className={styles.emptyText}>No rows yet. Press Add task and the clock starts.</p>
          </div>
        )
      ) : (
        <ul className={styles.rows} aria-label={landedBox ? "Landed rows" : "Timesheet rows"}>
          {rows.map((row) => (
            <TimesheetRow
              key={row.id}
              row={row}
              now={now}
              tasks={tasks}
              outcome={outcomes?.[row.id]}
              publishing={publishing}
              selected={selected?.includes(row.id) ?? false}
              onSelect={landedBox ? undefined : onSelect}
              onRename={onRename}
              onRetask={onRetask}
              onToggle={onToggle}
              onDelete={onDelete}
              onSetDate={onSetDate}
              onSetElapsed={onSetElapsed}
              onSetStart={onSetStart}
            />
          ))}
        </ul>
      )}

      {!landedBox && (
        <div className={styles.footer}>
          {chosen !== undefined && (
            <div className={styles.panel}>
              {outcomeLines.length > 0 && (
                <p className={styles.outcomeSummary} role="status">
                  {`${landedCount} landed, ${skippedCount} skipped, ${failed.length} failed.`}
                  {failed.length > 0 &&
                    ` Needs attention: ${failed.map(({ name }) => name).join(", ")}.`}
                </p>
              )}
              {deletedLanded && (
                <p className={styles.outcomeSummary}>
                  Time already in Accelo stays after a row is deleted.
                </p>
              )}
              {chosen.length > 0 && (
                <>
                  <ul className={styles.sendList} aria-label="Rows to publish">
                    {chosen.map((line) => (
                      <li key={line.id} className={styles.sendLine}>
                        <span className={styles.sendName}>{line.name}</span>
                        <span className={styles.sendTime}>{formatHeld(line.held)}</span>
                        {line.running && <span className={styles.sendRunning}>running</span>}
                      </li>
                    ))}
                  </ul>
                  <p className={styles.sendTotal}>
                    Total {formatHeld(chosen.reduce((sum, line) => sum + line.held, 0))}
                  </p>
                </>
              )}
            </div>
          )}
          <div className={styles.actions}>
            {publishing && onStop && (
              <button type="button" className={styles.stop} onClick={onStop}>
                Stop publish
              </button>
            )}
            <button
              type="button"
              className={styles.publish}
              aria-label="Publish to Accelo"
              aria-busy={publishing}
              disabled={nothingToSend || publishing || !onPublish}
              onClick={onPublish}
            >
              <svg viewBox="0 0 24 24" aria-hidden="true" className={styles.publishIcon}>
                <path d="M12 16V4" />
                <path d="M7.5 8.5L12 4l4.5 4.5" />
                <path d="M4 15v3.5a1.5 1.5 0 0 0 1.5 1.5h13a1.5 1.5 0 0 0 1.5-1.5V15" />
              </svg>
              {publishing ? "Publishing…" : "Publish to Accelo"}
            </button>
          </div>
        </div>
      )}
    </section>
  );
}
